import React, { useRef } from 'react';
import { Divider, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import useChangeMeterModalVisible from '../../../../hooks/useChangeMeterModalVisible';
import useFlatMeterMutations from '../../../../mutations/useFlatMeterMutations';
import MeterInfo from '../../MeterInfo/MeterInfo';
import MeterForm from '../../MeterForm/MeterForm';
import Footer from './Footer';
import InfoTitle from './InfoTitle';
import { setMeterModalData } from '../../../../store/actions/housingStockActions';
import { base64StrToFile, getSerialNumber, prepareValues } from './utils';
import useFlatMeterAttachment from '../../../../queries/useFlatMeterAttachment';
import { getBlobUrl } from '../../../../../../helpers/getBlobUrl';
import MeterAttachments from './MeterAttachments/MeterAttachments';
import './MeterModal.css'

const MeterModal = () => {
  const formRef = useRef();

  const { close } = useChangeMeterModalVisible();
  const { add, edit, isLoading } = useFlatMeterMutations();

  const dispatch = useDispatch();

  const meterModalData = useSelector(
    (state) => state.housingStockStore.meterModalData
  );
  const { CanEditIndividualMeters: canEdit } =
    useSelector((state) => state.authentication.userInfo) || {};

  const { meter, isInfo, addressId } = meterModalData || {};

  const { data: attachment, isFetching: isAttachmentFetching } =
    useFlatMeterAttachment(
      {
        meterId: meter?.id,
        fileName: meter?.actFileName,
      },
      {
        staleTime: Infinity,
        enabled: Boolean(meter?.id && meter?.actFileName),
        select: (data) => {
          if (data && data !== 'Error') {
            const ext = meter?.actFileName?.split('.').pop();
            const extText = ext ? `.${ext}` : '';
            return {
              file: base64StrToFile(data, `Акт${extText}`),
              url: getBlobUrl(data),
            };
          }
        },
      }
    );

  const serialNumText = getSerialNumber(meter?.serialNumber);

  const handleCancelBtnClick = () => {
    !isInfo && !meter
      ? close()
      : dispatch(setMeterModalData({ ...meterModalData, isInfo: true }));
  };

  const handleEditBtnClick = canEdit
    ? () => {
        dispatch(setMeterModalData({ ...meterModalData, isInfo: false }));
      }
    : undefined;

  const handleSubmit = () => {
    if (!formRef.current) return;

    formRef.current.validateFields(async (err, values) => {
      if (err) {
        return;
      }

      const preparedValues = prepareValues(values);
      const data = { ...preparedValues, addressId };

      meter
        ? await edit({ values: data, meterId: meter.id })
        : await add({ values: data });
      close();
    });
  };

  const booleanDisable = useSelector((state) => state.housingStockStore.meterModalData?.isInfo);

  return (
    <Modal
      className={meter?.statusId === 5 && booleanDisable ? 'VISIBLE' : null}
      visible={Boolean(meterModalData)}
      confirmLoading={isLoading}
      centered
      onCancel={close}
      maskClosable={false}
      destroyOnClose
      footer={
        isInfo ? (
          false
        ) : (
          <>
            <Footer
              onCancel={handleCancelBtnClick}
              onSubmit={handleSubmit}
              isEdit={Boolean(meter)}
              isLoading={isLoading}
            />
            {Boolean(meter) && (
              <>
                <Divider style={{ margin: '10px 0' }} />
                <MeterAttachments
                  meter={meter}
                  addressId={addressId}
                />
              </>
            )}
          </>
        )
      }
      title={
        isInfo ? (
          <InfoTitle
            onClick={(e) => {
              console.log(e)
              handleEditBtnClick()}}
            serialNumber={serialNumText}
          />
        ) : meter ? (
          `Изменить данные счетчика${serialNumText}`
        ) : (
          'Добавить счетчик'
        )
      }
    >
      {isInfo ? (
        <>
          <MeterInfo
            meter={meter}
            fileUrl={attachment?.url}
            fileLoading={isAttachmentFetching}
          />
          <MeterAttachments
            meter={meter}
            addressId={addressId}
          />
        </>
      ) : (
        <MeterForm
          wrappedComponentRef={formRef}
          meter={meter}
          act={attachment?.file}
        />
      )}
    </Modal>
  );
};

export default MeterModal;
